// Account settings (app side): the signed-in user edits their own display name
// and avatar. Writes go to /v1/account (backend → Casdoor). Users never see
// Casdoor's own account UI.

import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { api, ApiError, Me } from '../api';
import { TopBar } from '../components/TopBar';
import { Avatar } from '../ui/Avatar';

export function AccountPage() {
  const navigate = useNavigate();
  const [me, setMe] = useState<Me | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [avatar, setAvatar] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api
      .me()
      .then(setMe)
      .catch(() => void navigate('/'));
  }, [navigate]);
  useEffect(() => {
    api
      .account()
      .then((p) => {
        setFirstName(p.firstName);
        setLastName(p.lastName);
        setEmail(p.email);
        setAvatar(p.avatar);
        setLoaded(true);
      })
      .catch((e) => {
        if (e instanceof ApiError && e.status === 503) setUnavailable(true);
        setLoaded(true);
      });
  }, []);

  const onFirst = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSaved(false);
    setFirstName(e.target.value);
  }, []);
  const onLast = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSaved(false);
    setLastName(e.target.value);
  }, []);
  const onSignedOut = useCallback(() => void navigate('/'), [navigate]);
  const pickPhoto = useCallback(() => fileRef.current?.click(), []);
  const onPhoto = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setMsg('Uploading photo…');
    api
      .uploadAvatar(file)
      .then((r) => {
        setAvatar(r.avatar);
        setMe((m) => (m ? { ...m, avatarUrl: r.avatar } : m));
        setMsg('Photo updated.');
      })
      .catch((err) => setMsg(err instanceof Error ? err.message : 'Upload failed'));
  }, []);
  const save = useCallback(() => {
    setSaving(true);
    setMsg(null);
    api
      .saveAccount(firstName.trim(), lastName.trim())
      .then((p) => {
        setSaved(true);
        setMe((m) => (m ? { ...m, name: p.displayName } : m));
      })
      .catch((err) => setMsg(err instanceof Error ? err.message : 'Save failed'))
      .finally(() => setSaving(false));
  }, [firstName, lastName]);

  if (!me) return <div className="bz-state-page">Loading…</div>;

  return (
    <div className="bz-account">
      <TopBar me={me} section="Account" onSignedOut={onSignedOut} />
      <main className="bz-account-main">
        <h1>Account settings</h1>
        {unavailable ? (
          <p className="bz-account-note">Profile editing isn’t available for this account.</p>
        ) : (
          <section className="bz-account-card">
            <div className="bz-account-avatar">
              <Avatar name={me.name} identity={me.id} isSelf size={92} photoUrl={avatar || me.avatarUrl} />
              <div>
                <button className="bz-btn" onClick={pickPhoto}>
                  Change photo
                </button>
                <p className="bz-account-hint">PNG, JPEG, WebP or GIF · up to 5&nbsp;MB.</p>
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPhoto} />
              </div>
            </div>

            <div className="bz-account-field">
              <label>First name</label>
              <input value={firstName} onChange={onFirst} maxLength={64} disabled={!loaded} />
            </div>
            <div className="bz-account-field">
              <label>Last name</label>
              <input value={lastName} onChange={onLast} maxLength={64} disabled={!loaded} />
            </div>
            <div className="bz-account-field">
              <label>Email</label>
              <input value={email} readOnly disabled />
            </div>

            {msg && <p className="bz-account-note">{msg}</p>}
            <div className="bz-account-actions">
              <button className="bz-btn bz-btn-primary" onClick={save} disabled={saving || !loaded}>
                {saving ? 'Saving…' : 'Save changes'}
              </button>
              {saved && <span className="bz-account-saved">Saved ✓</span>}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
